import React, { useRef, useEffect, useState, useContext } from "react";
import { createPortal } from "react-dom";
import { AnimatePhaseContext } from "../ui/AnimatePhaseContext";

interface FilterPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  children: React.ReactNode;
  ariaLabel: string;
  /** When set, marks the popover root with data-shortcut-group for the overlay */
  groupId?: string;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  anchorRef,
  onClose,
  children,
  ariaLabel,
  groupId,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const phase = useContext(AnimatePhaseContext);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [anchorRef]);

  // After the popover renders, clamp it so it doesn't overflow the right edge
  useEffect(() => {
    if (!popoverRef.current || !anchorRef.current) return;
    const popoverWidth = popoverRef.current.offsetWidth;
    const rect = anchorRef.current.getBoundingClientRect();
    const rightEdge = rect.left + popoverWidth;
    if (rightEdge > window.innerWidth - 8) {
      setPosition({
        top: rect.bottom + 8,
        left: rect.right - popoverWidth,
      });
    }
    setVisible(true);
  }, [anchorRef]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, anchorRef]);

  if (!anchorRef.current) return null;

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={ariaLabel}
      {...(groupId
        ? {
            "data-shortcut-group": groupId,
            "data-shortcut-pad-top": "1",
            "data-shortcut-top-radius-boost": "8",
          }
        : {})}
      className={`fixed z-50 bg-warm-bg-card border border-warm-border rounded-lg shadow-xl p-3 min-w-[200px] origin-top-left ${phase === "exit" ? "animate-popover-out" : "animate-popover-in"}`}
      style={{
        top: position.top,
        left: position.left,
        visibility: visible ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body,
  );
};

export default FilterPopover;
